/**
 * Tasks API (tied to techforce_portal tasks and task_tags).
 */

import { API_BASE } from '../config/apiBase';
import { AttachmentVoidFields, patchAttachmentVoid } from './attachmentVoid';

export interface TaskAssignee {
  user_id: number;
  name: string;
}

export interface TaskRow {
  id: number;
  name: string;
  status: 'Unassigned' | 'To-Do' | 'In Progress' | 'On Hold' | 'Completed' | 'Void';
  assigned_to_id: number | null;
  /** First assignee’s user id (legacy / convenience). */
  assigned_to_user_id: number | null;
  /** Comma-separated names of all assignees. */
  assigned_to_name: string | null;
  /** All people assigned to the task (ordered). */
  assignees?: TaskAssignee[];
  assigner_id: number | null;
  assigner_name: string | null;
  client_id: number | null;
  client_company: string | null;
  start_date: string | null;
  due_date: string | null;
  description: string | null;
  notes: string | null;
  priority: string | null;
  tags: string[];
  created_at: string;
  updated_at: string;
  project_id?: number | null;
  project_board_column_id?: string | null;
  project_name?: string | null;
  event_id?: number | null;
  event_name?: string | null;
  /** Task that should be completed before this one (optional). */
  depends_on_task_id?: number | null;
  depends_on_task_name?: string | null;
  /** Status of the depended-on task (when loaded with dep join). */
  depends_on_task_status?: TaskRow['status'] | null;
  /** Optional 0–100 completion; null when unset. */
  progress_percent?: number | null;
  /** Present when task was loaded with viewerUserId (task detail / watch UI). */
  is_watched?: boolean;
  /** Personal priority for users with the Ried role only; omitted for other users. */
  ried_priority?: string | null;
}

export interface TasksSections {
  todo: TaskRow[];
  inProgress: TaskRow[];
  unassigned: TaskRow[];
  onHold: TaskRow[];
  completed: TaskRow[];
  watched: TaskRow[];
}

export async function fetchTasks(userId: number): Promise<TasksSections> {
  const res = await fetch(`${API_BASE}/api/tasks?userId=${encodeURIComponent(userId)}`);
  if (!res.ok) throw new Error('Failed to fetch tasks');
  const data = await res.json();
  return {
    todo: data.todo ?? [],
    inProgress: data.inProgress ?? [],
    unassigned: data.unassigned ?? [],
    onHold: data.onHold ?? [],
    completed: data.completed ?? [],
    watched: data.watched ?? [],
  };
}

/** Admin only: fetch all tasks (for dashboard table). */
export async function fetchAllTasks(adminUserId: number): Promise<TaskRow[]> {
  const res = await fetch(`${API_BASE}/api/tasks/all?admin_user_id=${encodeURIComponent(adminUserId)}`);
  if (!res.ok) throw new Error('Failed to fetch all tasks');
  const data = await res.json();
  return data.tasks ?? [];
}

/** Tasks that share roles with the user, excluding those in their todo / in progress / unassigned (for Task Board table). */
export async function fetchBoardTasks(userId: number): Promise<TaskRow[]>;
/** Project or event board task list (event tab uses event_id; project tab uses project_id). */
export async function fetchBoardTasks(
  viewerUserId: number,
  opts: { projectId: number; eventId?: number }
): Promise<TaskRow[]>;
export async function fetchBoardTasks(
  userId: number,
  opts?: { projectId: number; eventId?: number }
): Promise<TaskRow[]> {
  if (opts != null) {
    if (opts.eventId != null) {
      return fetchEventBoardTasks(opts.eventId, userId);
    }
    return fetchProjectBoardTasks(opts.projectId, userId);
  }
  const res = await fetch(`${API_BASE}/api/tasks/board?userId=${encodeURIComponent(userId)}`);
  if (!res.ok) throw new Error('Failed to fetch board tasks');
  const data = await res.json();
  return data.tasks ?? [];
}

/** Tasks linked to a project (Kanban board). */
export async function fetchProjectBoardTasks(projectId: number, viewerUserId: number): Promise<TaskRow[]> {
  const res = await fetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/tasks?viewerUserId=${encodeURIComponent(viewerUserId)}`
  );
  if (!res.ok) throw new Error('Failed to fetch project tasks');
  const data = await res.json();
  return data.tasks ?? [];
}

/** Tasks linked to an event (by event_id, plus legacy rows on the event backing project). */
export async function fetchEventBoardTasks(eventId: number, viewerUserId: number): Promise<TaskRow[]> {
  const res = await fetch(
    `${API_BASE}/api/events/${encodeURIComponent(eventId)}/tasks?viewerUserId=${encodeURIComponent(viewerUserId)}`
  );
  if (!res.ok) throw new Error('Failed to fetch event tasks');
  const data = await res.json();
  return data.tasks ?? [];
}

export async function fetchTask(id: number, viewerUserId?: number): Promise<TaskRow | null> {
  const q =
    viewerUserId != null && Number.isInteger(viewerUserId)
      ? `?viewerUserId=${encodeURIComponent(String(viewerUserId))}`
      : '';
  const res = await fetch(`${API_BASE}/api/tasks/${id}${q}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch task');
  return res.json();
}

export async function watchTask(taskId: number, userId: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/watch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || 'Failed to watch task');
  }
}

export async function unwatchTask(taskId: number, userId: number): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/watch?user_id=${encodeURIComponent(String(userId))}`,
    { method: 'DELETE' }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || 'Failed to unwatch task');
  }
}

/** Ried role: set or clear personal priority for this task (only stored for the acting user). Pass null to clear. */
export async function updateTaskRiedPriority(
  taskId: number,
  actingUserId: number,
  riedPriority: 'Low' | 'Medium' | 'High' | 'Urgent' | null
): Promise<TaskRow> {
  const res = await fetch(`${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/ried-priority`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ acting_user_id: actingUserId, ried_priority: riedPriority }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to update Ried priority');
  return data as TaskRow;
}

export interface CreateTaskPayload {
  name: string;
  status?: string;
  /** Prefer this for multiple assignees. */
  assigned_to_user_ids?: number[];
  assigned_to_user_id?: number;
  assigned_by_user_id?: number;
  client_id?: number;
  tags: string[];
  start_date?: string;
  due_date?: string;
  description?: string;
  notes?: string;
  priority?: string;
  project_id?: number;
  event_id?: number;
  project_board_column_id?: string;
  depends_on_task_id?: number | null;
  progress_percent?: number | null;
}

export async function createTask(payload: CreateTaskPayload): Promise<TaskRow> {
  const res = await fetch(`${API_BASE}/api/tasks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create task');
  return data;
}

export interface UpdateTaskPayload {
  name?: string;
  status?: string;
  /** Replaces the full assignee list when set. */
  assigned_to_user_ids?: number[] | null;
  assigned_to_user_id?: number | null;
  assigned_by_user_id?: number | null;
  client_id?: number | null;
  tags?: string[];
  start_date?: string | null;
  due_date?: string | null;
  description?: string | null;
  notes?: string | null;
  priority?: string | null;
  /** Required in addition to status: 'Void' when voiding a task. */
  void_reason?: string;
  project_id?: number | null;
  event_id?: number | null;
  project_board_column_id?: string | null;
  /** Required when moving on a board, or when attaching/removing/changing `project_id`. */
  acting_user_id?: number;
  depends_on_task_id?: number | null;
  /** Integer 0–100, or null to clear. */
  progress_percent?: number | null;
}

export async function updateTask(id: number, payload: UpdateTaskPayload): Promise<TaskRow> {
  const res = await fetch(`${API_BASE}/api/tasks/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update task');
  return data;
}

/** Task note (from task_notes table). */
export interface TaskNoteRow {
  id: number;
  task_id: number;
  user_id: number | null;
  submitted_by: string | null;
  note: string;
  created_at: string;
}

export async function fetchTaskNotes(taskId: number): Promise<TaskNoteRow[]> {
  const res = await fetch(`${API_BASE}/api/tasks/${taskId}/notes`);
  if (!res.ok) throw new Error('Failed to fetch task notes');
  const data = await res.json();
  return data.notes ?? [];
}

export async function createTaskNote(taskId: number, payload: { note: string; user_id: number }): Promise<TaskNoteRow> {
  const res = await fetch(`${API_BASE}/api/tasks/${taskId}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to add note');
  return data;
}

export async function updateTaskNote(
  taskId: number,
  noteId: number,
  payload: { note: string; user_id: number }
): Promise<TaskNoteRow> {
  const res = await fetch(`${API_BASE}/api/tasks/${taskId}/notes/${noteId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to update note');
  return data;
}

export async function deleteTaskNote(taskId: number, noteId: number, userId: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/tasks/${taskId}/notes/${noteId}?user_id=${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to delete note');
}

/** Task attachment (file) — from task_attachments table. */
export interface TaskAttachmentRow extends AttachmentVoidFields {
  id: number;
  task_id: number;
  original_file_name: string;
  stored_file_name: string;
  uploaded_by_user_id: number | null;
  uploaded_by: string | null;
  created_at: string;
}

export async function fetchTaskAttachments(taskId: number): Promise<TaskAttachmentRow[]> {
  const res = await fetch(`${API_BASE}/api/tasks/${taskId}/attachments`);
  if (!res.ok) throw new Error('Failed to fetch task attachments');
  const data = await res.json();
  return data.attachments ?? [];
}

export async function uploadTaskAttachment(
  taskId: number,
  file: File,
  userId: number | undefined
): Promise<TaskAttachmentRow> {
  const form = new FormData();
  form.append('file', file);
  if (userId != null) form.append('user_id', String(userId));
  const res = await fetch(`${API_BASE}/api/tasks/${taskId}/attachments`, {
    method: 'POST',
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to upload file');
  return data;
}

/** Returns the URL to download an attachment (open in new tab or use as href). */
export function taskAttachmentDownloadUrl(taskId: number, attachmentId: number): string {
  return `${API_BASE}/api/tasks/${taskId}/attachments/${attachmentId}/download`;
}

/** Sets a file aside with a reason. The file stays on the task and can be restored. */
export function voidTaskAttachment(
  taskId: number,
  attachmentId: number,
  reason: string,
  userId?: number | null
): Promise<TaskAttachmentRow> {
  return patchAttachmentVoid(`${API_BASE}/api/tasks/${taskId}/attachments/${attachmentId}`, reason, userId);
}

export function unvoidTaskAttachment(
  taskId: number,
  attachmentId: number,
  userId?: number | null
): Promise<TaskAttachmentRow> {
  return patchAttachmentVoid(`${API_BASE}/api/tasks/${taskId}/attachments/${attachmentId}`, null, userId);
}

export interface TaskActivityRow {
  id: number;
  task_id: number;
  actor_user_id: number | null;
  event_type: string;
  summary: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
  actor_username: string | null;
}

export async function fetchTaskActivity(taskId: number): Promise<TaskActivityRow[]> {
  const res = await fetch(`${API_BASE}/api/tasks/${taskId}/activity`);
  if (!res.ok) throw new Error('Failed to fetch task activity');
  const data = await res.json();
  return data.activity ?? [];
}

export interface TaskSubtaskRow {
  id: number;
  parent_task_id: number;
  title: string;
  is_completed: boolean;
  sort_order: number;
  assignee_user_id: number | null;
  assignee_name: string | null;
  created_at: string;
  updated_at: string;
}

export async function fetchTaskSubtasks(taskId: number): Promise<TaskSubtaskRow[]> {
  const res = await fetch(`${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/subtasks`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to fetch subtasks');
  return (data as { subtasks?: TaskSubtaskRow[] }).subtasks ?? [];
}

export async function createTaskSubtask(
  taskId: number,
  payload: { user_id: number; title: string; assignee_user_id?: number | null; sort_order?: number }
): Promise<TaskSubtaskRow> {
  const res = await fetch(`${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/subtasks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to create subtask');
  return data as TaskSubtaskRow;
}

export async function updateTaskSubtask(
  taskId: number,
  subtaskId: number,
  payload: {
    user_id: number;
    title?: string;
    is_completed?: boolean;
    assignee_user_id?: number | null;
    sort_order?: number;
  }
): Promise<TaskSubtaskRow> {
  const res = await fetch(
    `${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/subtasks/${encodeURIComponent(subtaskId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to update subtask');
  return data as TaskSubtaskRow;
}

export async function deleteTaskSubtask(taskId: number, subtaskId: number, userId: number): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/subtasks/${encodeURIComponent(subtaskId)}?user_id=${encodeURIComponent(String(userId))}`,
    { method: 'DELETE' }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to delete subtask');
}
